// apps/web/src/pages/admin-perfis-page.tsx — quick 261006-lf9; redesenho mestre-detalhe.
// Perfis e permissões: o que cada perfil permite, quem usa cada um, catálogo
// em pt-BR e gestão (criar, editar, excluir, conceder/revogar e atribuir).
// Camadas: helpers puros, componentes de apresentação (props) e o container.
// Ids de perfil e de usuário são só chave interna — nunca texto visível.
// Layout: em md+ a página não rola; a lista de perfis e o painel de detalhe rolam
// cada um por si. No celular, lista e detalhe alternam com um "Voltar aos perfis".
import { ArrowLeft, ChevronDown, Info, Search } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type ReactElement } from 'react';
import { ExcluirPerfilDialog, PerfilFormDialog } from '../components/perfis/perfil-dialogs';
import {
  avisoTrocaDePerfil,
  corpoTrocaDePerfil,
  mensagemDoServidor,
  type PerfilResumo,
  type PessoaDoPerfil,
} from '../components/perfis/comum';
import { CatalogoPermissoesConteudo, PessoasPermissoes } from '../components/perfis/quem-tem-o-que';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { normalizarBusca, rotuloAlteracoes } from '../components/users/permission-matrix-estado';
import { PermissionMatrix } from '../components/users/permission-matrix';
import { UserDialog } from '../components/users/user-dialog';
import { apiMutate } from '../lib/csrf-fetch';
import { rotuloStatus } from '../lib/rotulos';
import { CampoSelect } from '../components/ui/campo-select';

export { corpoTrocaDePerfil };
export type { PerfilResumo, PessoaDoPerfil };

const ERRO_CARGA = 'Não foi possível carregar perfis e pessoas. Verifique a conexão e tente novamente.';
const ERRO_ACESSO = 'Acesso negado. Apenas quem gerencia usuários e perfis pode abrir esta tela.';

const FOCO = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring';
const BOTAO_PRIMARIO =
  'min-h-[44px] rounded bg-primary px-4 text-sm font-semibold text-primary-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-50';
const BOTAO_SECUNDARIO =
  'min-h-[44px] rounded border border-border px-4 text-sm font-semibold text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-50';
const CAMPO =
  'min-h-[44px] w-full rounded border border-input bg-background px-4 text-sm text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-60';

/** Chave do localStorage que lembra se o aviso "Como as mudanças chegam" está aberto. */
export const CHAVE_AVISO_MUDANCAS = 'pieltec.admin-perfis.como-mudancas-aberto';

/** "1 permissão", "3 permissões" e "Nenhuma permissão". */
export function rotuloContagem(quantidade: number, singular: string, plural: string): string {
  if (quantidade === 0) return `Nenhuma ${singular}`;
  return `${quantidade} ${quantidade === 1 ? singular : plural}`;
}

/** Pessoas do perfil, em ordem alfabética pelo nome (pt-BR). */
export function pessoasDoPerfil(usuarios: readonly PessoaDoPerfil[], roleId: string): readonly PessoaDoPerfil[] {
  return usuarios
    .filter((usuario) => usuario.role.id === roleId)
    .sort((a, b) => (a.displayName || a.username).localeCompare(b.displayName || b.username, 'pt-BR'));
}

/** Pessoas ATIVAS que ainda não usam o perfil — candidatas a recebê-lo. */
export function candidatasAoPerfil(usuarios: readonly PessoaDoPerfil[], roleId: string): readonly PessoaDoPerfil[] {
  return usuarios
    .filter((usuario) => usuario.status === 'ACTIVE' && usuario.role.id !== roleId)
    .sort((a, b) => (a.displayName || a.username).localeCompare(b.displayName || b.username, 'pt-BR'));
}

/** Filtra a lista de perfis pelo nome, sem diferenciar acento nem caixa. */
export function filtrarPerfis(perfis: readonly PerfilResumo[], busca: string): readonly PerfilResumo[] {
  const termo = normalizarBusca(busca);
  if (termo === '') return perfis;
  return perfis.filter((perfil) => normalizarBusca(perfil.nome).includes(termo));
}

/** "23 permissões · 1 pessoa ativa · 1 inativa". */
export function resumoDoPerfil(perfil: Pick<PerfilResumo, 'permissoes' | 'usuariosAtivos' | 'usuariosInativos'>): string {
  const partes = [
    rotuloContagem(perfil.permissoes.length, 'permissão', 'permissões'),
    rotuloContagem(perfil.usuariosAtivos, 'pessoa ativa', 'pessoas ativas'),
  ];
  if (perfil.usuariosInativos > 0) partes.push(rotuloContagem(perfil.usuariosInativos, 'inativa', 'inativas'));
  return partes.join(' · ');
}

/** Índice de destino da navegação por teclado na lista (setas, Home e End); nulo para outras teclas. */
export function proximoIndiceDaLista(tecla: string, atual: number, total: number): number | null {
  if (total === 0) return null;
  if (tecla === 'ArrowDown') return Math.min(atual + 1, total - 1);
  if (tecla === 'ArrowUp') return Math.max(atual - 1, 0);
  if (tecla === 'Home') return 0;
  if (tecla === 'End') return total - 1;
  return null;
}

export interface ListaPerfisProps {
  readonly perfis: readonly PerfilResumo[];
  readonly selecionadoId: string | null;
  readonly onSelecionar: (perfilId: string) => void;
}

/** Lista compacta: uma linha por perfil (nome, selo do sistema e contagens). A descrição fica só no detalhe. */
export function ListaPerfis({ perfis, selecionadoId, onSelecionar }: ListaPerfisProps): ReactElement {
  const lista = useRef<HTMLUListElement>(null);

  function navegar(evento: KeyboardEvent<HTMLUListElement>): void {
    const botoes = Array.from(lista.current?.querySelectorAll<HTMLButtonElement>('button[data-perfil]') ?? []);
    const atual = botoes.findIndex((botao) => botao === document.activeElement);
    const destino = proximoIndiceDaLista(evento.key, atual < 0 ? 0 : atual, botoes.length);
    if (destino === null) return;
    evento.preventDefault();
    botoes[destino]?.focus();
  }

  if (perfis.length === 0) {
    return <p className="px-2 py-4 text-sm text-muted-foreground">Nenhum perfil encontrado.</p>;
  }
  return (
    <ul ref={lista} className="m-0 flex list-none flex-col gap-1 p-0" aria-label="Perfis" onKeyDown={navegar}>
      {perfis.map((perfil) => {
        const selecionado = perfil.id === selecionadoId;
        return (
          <li key={perfil.id}>
            <button
              type="button"
              data-perfil=""
              aria-pressed={selecionado}
              onClick={() => onSelecionar(perfil.id)}
              className={`flex min-h-[44px] w-full flex-col items-start justify-center rounded-md border-l-4 px-2 py-1 text-left hover:bg-muted ${FOCO} ${
                selecionado ? 'border-primary bg-muted text-foreground' : 'border-transparent text-foreground'
              }`}
            >
              <span className="flex w-full min-w-0 items-center gap-2">
                <span className="min-w-0 truncate text-sm font-semibold">{perfil.nome}</span>
                {perfil.sistema ? <Badge variant="secondary" title="Perfil do sistema">sistema</Badge> : null}
              </span>
              <span className="w-full truncate text-xs text-muted-foreground">{resumoDoPerfil(perfil)}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export interface PessoasDoPerfilProps {
  readonly pessoas: readonly PessoaDoPerfil[];
  readonly onTrocarPerfil?: (pessoa: PessoaDoPerfil) => void;
}

export function PessoasDoPerfil({ pessoas, onTrocarPerfil }: PessoasDoPerfilProps): ReactElement {
  if (pessoas.length === 0) {
    return <p className="text-sm text-muted-foreground">Nenhuma pessoa usa este perfil.</p>;
  }
  return (
    <ul className="m-0 flex list-none flex-col gap-2 p-0">
      {pessoas.map((pessoa) => (
        <li key={pessoa.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-card px-4 py-2 text-card-foreground">
          <span className="flex min-w-0 flex-col">
            <span className="break-words font-semibold">{pessoa.displayName || pessoa.username}</span>
            <span className="break-words text-sm text-muted-foreground">
              {pessoa.username} · {rotuloStatus(pessoa.status)}
            </span>
          </span>
          {onTrocarPerfil ? (
            <button type="button" onClick={() => onTrocarPerfil(pessoa)} className={BOTAO_SECUNDARIO}>
              Trocar perfil
            </button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

export interface AtribuirPerfilConteudoProps {
  readonly nomeDoPerfil: string;
  readonly candidatas: readonly PessoaDoPerfil[];
  readonly pessoaId: string;
  readonly enviando: boolean;
  readonly onPessoa: (usuarioId: string) => void;
  readonly onAtribuir: () => void;
}

/** Bloco "Atribuir este perfil a uma pessoa", com o aviso de desconexão (ADR-003). */
export function AtribuirPerfilConteudo({ nomeDoPerfil, candidatas, pessoaId, enviando, onPessoa, onAtribuir }: AtribuirPerfilConteudoProps): ReactElement {
  const escolhida = candidatas.find((pessoa) => pessoa.id === pessoaId);
  return (
    <section className="flex flex-col gap-2 rounded-lg border border-border bg-card p-4 text-card-foreground" aria-label="Atribuir este perfil a uma pessoa">
      <h3 className="text-base font-semibold text-foreground">Atribuir este perfil a uma pessoa</h3>
      {candidatas.length === 0 ? (
        <p className="text-sm text-muted-foreground">Não há outras pessoas ativas para receber este perfil.</p>
      ) : (
        <>
          <label htmlFor="atribuir-pessoa" className="text-sm font-semibold text-foreground">Pessoa</label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <CampoSelect
              id="atribuir-pessoa"
              value={pessoaId}
              disabled={enviando}
              onValueChange={onPessoa}
              options={[
                { value: '', label: 'Escolha uma pessoa' },
                ...candidatas.map((pessoa) => ({ value: pessoa.id, label: `${pessoa.displayName || pessoa.username} (perfil atual: ${pessoa.role.name})` })),
              ]}
            />
            <button type="button" disabled={enviando || !escolhida} onClick={onAtribuir} className={`${BOTAO_PRIMARIO} shrink-0`}>
              {enviando ? 'Atribuindo…' : 'Atribuir perfil'}
            </button>
          </div>
          {escolhida ? (
            <p className="text-sm text-muted-foreground" role="status">
              {avisoTrocaDePerfil(escolhida.displayName || escolhida.username, nomeDoPerfil)}
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}

/**
 * Lembrar a escolha no navegador só fora de produção: em produção (SEC-001 §13.4)
 * nenhum dado vai para o Web Storage, e o aviso volta recolhido a cada visita.
 */
export const LEMBRAR_AVISO_NO_NAVEGADOR = !import.meta.env.PROD;

function lerAvisoAberto(): boolean {
  if (!LEMBRAR_AVISO_NO_NAVEGADOR) return false;
  try {
    return window.localStorage.getItem(CHAVE_AVISO_MUDANCAS) === 'sim';
  } catch {
    return false;
  }
}

function gravarAvisoAberto(aberto: boolean): void {
  if (!LEMBRAR_AVISO_NO_NAVEGADOR) return;
  try {
    if (aberto) window.localStorage.setItem(CHAVE_AVISO_MUDANCAS, 'sim');
    else window.localStorage.removeItem(CHAVE_AVISO_MUDANCAS);
  } catch {
    // Armazenamento indisponível (aba anônima, bloqueio): o aviso só não lembra a escolha.
  }
}

/** Aviso de uma linha; o detalhe abre num toggle e a escolha fica lembrada (fechado por padrão). */
export function ComoAsMudancasChegam({ abertoInicial = false }: { readonly abertoInicial?: boolean }): ReactElement {
  const [aberto, setAberto] = useState(abertoInicial);
  useEffect(() => { if (lerAvisoAberto()) setAberto(true); }, []);

  function alternar(): void {
    setAberto((atual) => {
      gravarAvisoAberto(!atual);
      return !atual;
    });
  }

  return (
    <div className="rounded-md border border-border bg-card text-sm text-card-foreground" data-testid="como-mudancas-chegam">
      <button
        type="button"
        aria-expanded={aberto}
        aria-controls="como-mudancas-detalhe"
        onClick={alternar}
        className={`flex min-h-[44px] w-full items-center gap-2 rounded-md px-4 text-left ${FOCO}`}
      >
        <Info size={16} aria-hidden="true" className="shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 font-semibold text-foreground">Como as mudanças chegam às pessoas</span>
        <span className="shrink-0 text-muted-foreground">{aberto ? 'Ocultar' : 'Ver detalhes'}</span>
        <ChevronDown size={16} aria-hidden="true" className={`shrink-0 text-muted-foreground transition-transform motion-reduce:transition-none ${aberto ? 'rotate-180' : ''}`} />
      </button>
      <ul id="como-mudancas-detalhe" hidden={!aberto} className="m-0 list-disc px-4 pb-2 pl-8 text-muted-foreground">
        <li>Permissões de um perfil valem na próxima ação de quem já está conectado; o menu dessas pessoas se atualiza quando recarregarem a página.</li>
        <li>Trocar o perfil de uma pessoa a desconecta: ela precisa entrar de novo.</li>
      </ul>
    </div>
  );
}

export const MOTIVO_PERFIL_DO_SISTEMA = 'Perfis do sistema não podem ser renomeados nem excluídos.';
const ID_MOTIVO_PERFIL_DO_SISTEMA = 'perfil-sistema-motivo';

/**
 * Ações de cabeçalho do perfil selecionado. Perfis do sistema já chegam com as ações desabilitadas e com o
 * motivo visível e ligado por aria-describedby/title (o servidor também recusa; o diálogo não é o único bloqueio).
 */
export function AcoesDoPerfil({ perfil, onEditar, onExcluir }: {
  readonly perfil: Pick<PerfilResumo, 'sistema'>;
  readonly onEditar?: () => void;
  readonly onExcluir?: () => void;
}): ReactElement {
  const bloqueado = perfil.sistema;
  const motivo = bloqueado ? { 'aria-describedby': ID_MOTIVO_PERFIL_DO_SISTEMA, title: MOTIVO_PERFIL_DO_SISTEMA } : {};
  return (
    <div className="flex flex-col items-start gap-1 sm:items-end">
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={onEditar} disabled={bloqueado} {...motivo} className={BOTAO_SECUNDARIO}>Editar nome e descrição</button>
        <button type="button" onClick={onExcluir} disabled={bloqueado} {...motivo} className={BOTAO_SECUNDARIO}>Excluir perfil</button>
      </div>
      {bloqueado ? <p id={ID_MOTIVO_PERFIL_DO_SISTEMA} className="text-sm text-muted-foreground">{MOTIVO_PERFIL_DO_SISTEMA}</p> : null}
    </div>
  );
}

export interface ConfirmarDescarteProps {
  readonly aberto: boolean;
  readonly alteracoes: number;
  readonly onDescartar: () => void;
  readonly onCancelar: () => void;
}

/** "Descartar alterações?" ao trocar de perfil ou de aba com a matriz alterada. */
export function ConfirmarDescarte({ aberto, alteracoes, onDescartar, onCancelar }: ConfirmarDescarteProps): ReactElement {
  return (
    <AlertDialog open={aberto} onOpenChange={(proximo) => { if (!proximo) onCancelar(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Descartar alterações?</AlertDialogTitle>
          <AlertDialogDescription>
            {`Há ${rotuloAlteracoes(alteracoes)} nas permissões deste perfil. Se continuar, elas serão perdidas.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="min-h-[44px]">Continuar editando</AlertDialogCancel>
          <AlertDialogAction className="min-h-[44px]" onClick={onDescartar}>Descartar alterações</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

type AbaDoPerfil = 'permissoes' | 'pessoas';
type AbaDaPagina = 'perfis' | 'pessoas' | 'catalogo';

export function AdminPerfisPage(): ReactElement {
  const [perfis, setPerfis] = useState<readonly PerfilResumo[]>([]);
  const [usuarios, setUsuarios] = useState<readonly PessoaDoPerfil[]>([]);
  const [catalogo, setCatalogo] = useState<readonly string[]>([]);
  const [selecionadoId, setSelecionadoId] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [erroAcao, setErroAcao] = useState<string | null>(null);
  const [criando, setCriando] = useState(false);
  const [editando, setEditando] = useState<PerfilResumo | null>(null);
  const [excluindo, setExcluindo] = useState<PerfilResumo | null>(null);
  const [trocando, setTrocando] = useState<PessoaDoPerfil | null>(null);
  const [pessoaAtribuirId, setPessoaAtribuirId] = useState('');
  const [atribuindo, setAtribuindo] = useState(false);
  const [buscaPerfil, setBuscaPerfil] = useState('');
  const [abaPagina, setAbaPagina] = useState<AbaDaPagina>('perfis');
  const [abaPerfil, setAbaPerfil] = useState<AbaDoPerfil>('permissoes');
  const [vistaMovel, setVistaMovel] = useState<'lista' | 'detalhe'>('lista');
  const [alteracoes, setAlteracoes] = useState(0);
  const [pendente, setPendente] = useState<(() => void) | null>(null);

  const carregar = useCallback(async (): Promise<void> => {
    setCarregando(true);
    setErro(null);
    try {
      const [resumoRes, usuariosRes, catalogoRes] = await Promise.all([
        fetch('/api/v1/authorization/roles/resumo', { credentials: 'include' }),
        fetch('/api/v1/users', { credentials: 'include' }),
        fetch('/api/v1/authorization/permissions', { credentials: 'include' }),
      ]);
      if ([resumoRes.status, usuariosRes.status, catalogoRes.status].some((status) => status === 401 || status === 403)) {
        throw new Error(ERRO_ACESSO);
      }
      if (!resumoRes.ok || !usuariosRes.ok || !catalogoRes.ok) throw new Error(ERRO_CARGA);
      const resumo = (await resumoRes.json()) as { perfis?: PerfilResumo[] };
      const lista = (await usuariosRes.json()) as { users?: PessoaDoPerfil[] };
      const permissoes = (await catalogoRes.json()) as { permissions?: string[] };
      const perfisCarregados = resumo.perfis ?? [];
      setPerfis(perfisCarregados);
      setUsuarios(lista.users ?? []);
      setCatalogo(permissoes.permissions ?? []);
      setSelecionadoId((atual) => (atual && perfisCarregados.some((perfil) => perfil.id === atual) ? atual : (perfisCarregados[0]?.id ?? null)));
    } catch (cause) {
      setErro(cause instanceof Error ? cause.message : ERRO_CARGA);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  // Fechar ou recarregar a aba com permissões não salvas pede confirmação do navegador.
  useEffect(() => {
    if (alteracoes === 0) return undefined;
    const aoSair = (evento: BeforeUnloadEvent): void => { evento.preventDefault(); };
    window.addEventListener('beforeunload', aoSair);
    return () => window.removeEventListener('beforeunload', aoSair);
  }, [alteracoes]);

  const selecionado = perfis.find((perfil) => perfil.id === selecionadoId) ?? null;
  const perfisVisiveis = filtrarPerfis(perfis, buscaPerfil);

  /** Executa a ação já, ou pede "Descartar alterações?" se a matriz tem mudanças não salvas. */
  function protegido(acao: () => void): void {
    if (alteracoes > 0) setPendente(() => acao);
    else acao();
  }

  function selecionarPerfil(perfilId: string): void {
    if (perfilId === selecionadoId) {
      setVistaMovel('detalhe');
      return;
    }
    protegido(() => {
      setSelecionadoId(perfilId);
      setPessoaAtribuirId('');
      setVistaMovel('detalhe');
    });
  }

  function sucesso(mensagem: string): void {
    setErroAcao(null);
    setAviso(mensagem);
    void carregar();
  }

  async function atribuir(): Promise<void> {
    const pessoa = usuarios.find((usuario) => usuario.id === pessoaAtribuirId);
    if (!pessoa || !selecionado) return;
    setAtribuindo(true);
    setErroAcao(null);
    setAviso(null);
    try {
      const resposta = await apiMutate(`/api/v1/users/${pessoa.id}`, { method: 'PATCH', body: corpoTrocaDePerfil(pessoa, selecionado.id) });
      if (!resposta.ok) {
        setErroAcao(mensagemDoServidor(resposta.status, await resposta.json().catch(() => ({}))));
        return;
      }
      setPessoaAtribuirId('');
      sucesso(`${pessoa.displayName || pessoa.username} agora usa o perfil ${selecionado.nome}. A pessoa foi desconectada e precisa entrar de novo.`);
    } catch {
      setErroAcao('Não foi possível concluir a operação. Nada foi alterado — tente novamente.');
    } finally {
      setAtribuindo(false);
    }
  }

  return (
    <div className="flex min-w-0 flex-col gap-4 md:h-[calc(100dvh-9rem)] md:min-h-[480px]">
      <header className="flex shrink-0 flex-col gap-1">
        <h1 className="text-2xl font-semibold text-foreground">Perfis e permissões</h1>
        <p className="text-sm text-muted-foreground">Veja o que cada perfil permite, quem usa cada um e conceda ou revogue permissões.</p>
      </header>

      <div className="shrink-0">
        <ComoAsMudancasChegam />
      </div>

      {erro ? (
        <div className="flex shrink-0 flex-col items-start gap-2 rounded-lg border border-danger p-4 text-danger" role="alert">
          <p>{erro}</p>
          <button type="button" onClick={() => void carregar()} className={BOTAO_PRIMARIO}>Tentar novamente</button>
        </div>
      ) : null}
      {erroAcao ? <div className="shrink-0 rounded-lg border border-danger px-4 py-2 text-sm text-danger" role="alert">{erroAcao}</div> : null}
      {aviso ? (
        <div className="flex shrink-0 items-center justify-between gap-2 rounded-lg border border-success px-4 text-sm text-foreground" role="status">
          <span className="py-2">{aviso}</span>
          <button type="button" onClick={() => setAviso(null)} className={`min-h-[44px] shrink-0 rounded px-2 text-sm font-semibold underline ${FOCO}`}>Fechar</button>
        </div>
      ) : null}

      {carregando && perfis.length === 0 && !erro ? <p className="text-sm text-muted-foreground">Carregando perfis…</p> : null}

      {perfis.length > 0 ? (
        <Tabs
          value={abaPagina}
          onValueChange={(valor) => protegido(() => setAbaPagina(valor as AbaDaPagina))}
          className="min-h-0 flex-1 gap-4"
        >
          <TabsList className="h-auto! shrink-0 flex-wrap">
            <TabsTrigger value="perfis" className="h-auto min-h-[44px] px-4">Perfis</TabsTrigger>
            <TabsTrigger value="pessoas" className="h-auto min-h-[44px] px-4">Pessoas</TabsTrigger>
            <TabsTrigger value="catalogo" className="h-auto min-h-[44px] px-4">Catálogo de permissões</TabsTrigger>
          </TabsList>

          <TabsContent value="perfis" className="min-h-0">
            <div className="grid gap-4 md:h-full md:grid-cols-[minmax(240px,320px)_minmax(0,1fr)] md:grid-rows-[minmax(0,1fr)]">
              <section
                aria-label="Lista de perfis"
                className={`${vistaMovel === 'detalhe' ? 'hidden md:flex' : 'flex'} min-h-0 flex-col gap-2 rounded-lg border border-border bg-card p-2 text-card-foreground`}
              >
                <div className="flex shrink-0 flex-col gap-2 p-1">
                  <button type="button" onClick={() => protegido(() => setCriando(true))} className={BOTAO_PRIMARIO}>Novo perfil</button>
                  <label className="relative">
                    <span className="sr-only">Buscar perfil pelo nome</span>
                    <Search size={16} aria-hidden="true" className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="search"
                      value={buscaPerfil}
                      placeholder="Buscar perfil"
                      onChange={(evento) => setBuscaPerfil(evento.target.value)}
                      className={`${CAMPO} pl-8`}
                    />
                  </label>
                </div>
                <div className="relative min-h-0 flex-1 overflow-y-auto p-1">
                  <ListaPerfis perfis={perfisVisiveis} selecionadoId={selecionadoId} onSelecionar={selecionarPerfil} />
                </div>
              </section>

              {selecionado ? (
                <section
                  className={`${vistaMovel === 'lista' ? 'hidden md:flex' : 'flex'} relative min-h-0 min-w-0 flex-col rounded-lg md:overflow-y-auto border border-border bg-card text-card-foreground`}
                  aria-label={`Detalhes do perfil ${selecionado.nome}`}
                  data-testid="perfil-detalhe"
                >
                  <Tabs
                    value={abaPerfil}
                    onValueChange={(valor) => protegido(() => setAbaPerfil(valor as AbaDoPerfil))}
                    className="gap-0"
                  >
                    <div className="z-20 flex flex-col gap-2 border-b border-border bg-card p-4 md:sticky md:top-0">
                      <button
                        type="button"
                        onClick={() => setVistaMovel('lista')}
                        className={`flex min-h-[44px] items-center gap-2 self-start rounded px-2 text-sm font-semibold text-foreground md:hidden ${FOCO}`}
                      >
                        <ArrowLeft size={16} aria-hidden="true" /> Voltar aos perfis
                      </button>
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="flex min-w-0 flex-col gap-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <h2 className="break-words text-xl font-semibold text-foreground">{selecionado.nome}</h2>
                            {selecionado.sistema ? <Badge variant="secondary">Perfil do sistema</Badge> : null}
                          </div>
                          {selecionado.descricao ? <p className="break-words text-sm text-muted-foreground">{selecionado.descricao}</p> : null}
                          <p className="text-xs text-muted-foreground">{resumoDoPerfil(selecionado)}</p>
                        </div>
                        <AcoesDoPerfil
                          perfil={selecionado}
                          onEditar={() => setEditando(selecionado)}
                          onExcluir={() => protegido(() => setExcluindo(selecionado))}
                        />
                      </div>
                      <TabsList className="h-auto! self-start">
                        <TabsTrigger value="permissoes" className="h-auto min-h-[44px] px-4">
                          Permissões
                        </TabsTrigger>
                        <TabsTrigger value="pessoas" className="h-auto min-h-[44px] px-4">
                          {`Pessoas (${selecionado.usuariosAtivos + selecionado.usuariosInativos})`}
                        </TabsTrigger>
                      </TabsList>
                    </div>

                    <TabsContent value="permissoes" className="px-4 pt-4">
                      <PermissionMatrix
                        key={selecionado.id}
                        variante="painel"
                        roleId={selecionado.id}
                        roleName={selecionado.nome}
                        onAlteracoes={setAlteracoes}
                        onSaveSuccess={() => void carregar()}
                      />
                    </TabsContent>

                    <TabsContent value="pessoas" className="flex flex-col gap-4 p-4">
                      <AtribuirPerfilConteudo
                        nomeDoPerfil={selecionado.nome}
                        candidatas={candidatasAoPerfil(usuarios, selecionado.id)}
                        pessoaId={pessoaAtribuirId}
                        enviando={atribuindo}
                        onPessoa={setPessoaAtribuirId}
                        onAtribuir={() => void atribuir()}
                      />
                      <div className="flex flex-col gap-2">
                        <h3 className="text-base font-semibold text-foreground">Pessoas com este perfil</h3>
                        <PessoasDoPerfil pessoas={pessoasDoPerfil(usuarios, selecionado.id)} onTrocarPerfil={setTrocando} />
                      </div>
                    </TabsContent>
                  </Tabs>
                </section>
              ) : null}
            </div>
          </TabsContent>

          <TabsContent value="pessoas" className="min-h-0 overflow-y-auto">
            <PessoasPermissoes usuarios={usuarios} onTrocarPerfil={setTrocando} />
          </TabsContent>

          <TabsContent value="catalogo" className="min-h-0 overflow-y-auto">
            <CatalogoPermissoesConteudo catalogo={catalogo} perfis={perfis} />
          </TabsContent>
        </Tabs>
      ) : null}

      <ConfirmarDescarte
        aberto={pendente !== null}
        alteracoes={alteracoes}
        onCancelar={() => setPendente(null)}
        onDescartar={() => {
          const acao = pendente;
          setPendente(null);
          setAlteracoes(0);
          acao?.();
        }}
      />
      <PerfilFormDialog
        aberto={criando || editando !== null}
        perfil={editando}
        perfis={perfis}
        onClose={() => { setCriando(false); setEditando(null); }}
        onSalvo={(resumo, modo) => {
          setCriando(false);
          setEditando(null);
          if (modo === 'criar') { setSelecionadoId(resumo.id); setVistaMovel('detalhe'); }
          sucesso(modo === 'criar' ? `Perfil ${resumo.nome} criado.` : `Perfil ${resumo.nome} atualizado.`);
        }}
      />
      <ExcluirPerfilDialog
        perfil={excluindo}
        onClose={() => setExcluindo(null)}
        onExcluido={(perfil) => {
          setExcluindo(null);
          sucesso(`Perfil ${perfil.nome} excluído.`);
        }}
      />
      <UserDialog
        open={trocando !== null}
        editUser={trocando}
        onClose={() => setTrocando(null)}
        onUserCreated={() => sucesso('Perfil da pessoa atualizado. Se o perfil mudou, ela foi desconectada e precisa entrar de novo.')}
      />
    </div>
  );
}
